import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ArrowRight } from 'lucide-react';
import './ScanCard.css';

/**
 * ScanCard Component
 * 
 * Displays individual scan information in a card format with:
 * - Scan metadata (URL, timestamp, status)
 * - Risk severity indicators
 * - Vulnerability statistics
 * - Expandable details section
 * - Responsive design
 * - Full accessibility support
 * 
 * @param {Object} props
 * @param {Object} props.scan - Scan data object
 * @param {Function} props.onClick - Click handler for card
 * @param {boolean} props.expandable - Enable expandable details
 */
export const ScanCard = ({ scan, onClick, expandable = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const {
    scan_id,
    target_url,
    status,
    progress,
    risk_score,
    critical_count = 0,
    high_count = 0,
    medium_count = 0,
    low_count = 0,
    info_count = 0,
    started_at,
    completed_at,
    created_at,
    scan_types = [],
    vulnerabilities = [],
  } = scan;

  /**
   * Get status badge configuration
   */
  const getStatusConfig = (status) => {
    const configs = {
      completed: { label: 'Completed', color: 'success', icon: '' },
      running: { label: 'Running', color: 'info', icon: '⟳' },
      in_progress: { label: 'In Progress', color: 'info', icon: '⟳' },
      failed: { label: 'Failed', color: 'danger', icon: '' },
      pending: { label: 'Pending', color: 'warning', icon: '○' },
      queued: { label: 'Queued', color: 'secondary', icon: '⋯' },
    };
    return configs[status] || configs.pending;
  };

  /**
   * Get risk level and color based on risk score
   */
  const getRiskConfig = (score) => {
    if (score === null || score === undefined) {
      return { level: 'Unknown', color: 'secondary', severity: 'unknown' };
    }
    
    if (score >= 9) return { level: 'Critical', color: 'critical', severity: 'critical' };
    if (score >= 7) return { level: 'High', color: 'high', severity: 'high' };
    if (score >= 4) return { level: 'Medium', color: 'medium', severity: 'medium' };
    if (score >= 1) return { level: 'Low', color: 'low', severity: 'low' };
    return { level: 'Info', color: 'info', severity: 'info' };
  };

  /**
   * Format timestamp to relative time
   */
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch (error) {
      return 'Invalid date';
    }
  };

  /**
   * Calculate total vulnerabilities
   */
  const totalVulnerabilities = critical_count + high_count + medium_count + low_count + info_count;

  /**
   * Handle card click
   */
  const handleCardClick = (e) => {
    // Don't trigger if clicking on expand button
    if (e.target.closest('.expand-button')) {
      return;
    }
    
    if (onClick) {
      onClick(scan_id, status);
    }
  };

  /**
   * Handle expand toggle
   */
  const handleExpandToggle = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const statusConfig = getStatusConfig(status);
  const riskConfig = getRiskConfig(risk_score);
  const isComplete = String(status || '').toLowerCase() === 'completed';

  return (
    <article
      className={`scan-card scan-card--${statusConfig.color} ${!isComplete ? 'scan-card--non-clickable' : ''}`}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick(e);
        }
      }}
      aria-label={`Scan for ${target_url}, status: ${statusConfig.label}`}
    >
      {/* Header */}
      <div className="scan-card__header">
        <div className="scan-card__title-group">
          <h3 className="scan-card__title" title={target_url}>
            {target_url}
          </h3>
          <div className="scan-card__meta">
            <span className="scan-card__scan-id" title={`Scan ID: ${scan_id}`}>
              {scan_id.substring(0, 8)}...
            </span>
          </div>
        </div>
        
        <div className="scan-card__badges">
          <span 
            className={`scan-card__status-badge scan-card__status-badge--${statusConfig.color}`}
            aria-label={`Status: ${statusConfig.label}`}
          >
            <span className="scan-card__status-icon" aria-hidden="true">
              {statusConfig.icon}
            </span>
            {statusConfig.label}
          </span>
        </div>
      </div>

      {/* Progress Bar (for running scans) */}
      {(status === 'running' || status === 'in_progress') && progress !== undefined && (
        <div className="scan-card__progress">
          <div 
            className="scan-card__progress-bar"
            style={{ width: `${progress}%` }}
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin="0"
            aria-valuemax="100"
            aria-label={`Scan progress: ${progress}%`}
          />
          <span className="scan-card__progress-text">{progress}%</span>
        </div>
      )}

      {/* Risk Score */}
      {risk_score !== null && risk_score !== undefined && (
        <div className="scan-card__risk">
          <div className={`scan-card__risk-badge scan-card__risk-badge--${riskConfig.color}`}>
            <span className="scan-card__risk-label">Risk Score</span>
            <span className="scan-card__risk-value">{risk_score.toFixed(1)}</span>
            <span className="scan-card__risk-level">{riskConfig.level}</span>
          </div>
        </div>
      )}

      {/* Vulnerability Statistics */}
      {totalVulnerabilities > 0 && (
        <div className="scan-card__stats">
          {critical_count > 0 && (
            <div className="scan-card__stat scan-card__stat--critical">
              <span className="scan-card__stat-value">{critical_count}</span>
              <span className="scan-card__stat-label">Critical</span>
            </div>
          )}
          {high_count > 0 && (
            <div className="scan-card__stat scan-card__stat--high">
              <span className="scan-card__stat-value">{high_count}</span>
              <span className="scan-card__stat-label">High</span>
            </div>
          )}
          {medium_count > 0 && (
            <div className="scan-card__stat scan-card__stat--medium">
              <span className="scan-card__stat-value">{medium_count}</span>
              <span className="scan-card__stat-label">Medium</span>
            </div>
          )}
          {low_count > 0 && (
            <div className="scan-card__stat scan-card__stat--low">
              <span className="scan-card__stat-value">{low_count}</span>
              <span className="scan-card__stat-label">Low</span>
            </div>
          )}
          {info_count > 0 && (
            <div className="scan-card__stat scan-card__stat--info">
              <span className="scan-card__stat-value">{info_count}</span>
              <span className="scan-card__stat-label">Info</span>
            </div>
          )}
        </div>
      )}

      {/* Scan Types */}
      {scan_types && scan_types.length > 0 && (
        <div className="scan-card__tags">
          {scan_types.slice(0, 3).map((type, index) => (
            <span key={index} className="scan-card__tag">
              {type}
            </span>
          ))}
          {scan_types.length > 3 && (
            <span className="scan-card__tag scan-card__tag--more">
              +{scan_types.length - 3} more
            </span>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="scan-card__footer">
        <div className="scan-card__timestamp">
          <span className="scan-card__timestamp-label">
            {completed_at ? 'Completed' : started_at ? 'Started' : 'Created'}:
          </span>
          <time 
            dateTime={completed_at || started_at || created_at}
            className="scan-card__timestamp-value"
          >
            {formatTimestamp(completed_at || started_at || created_at)}
          </time>
        </div>

        <div className="scan-card__actions">
          {expandable && vulnerabilities && vulnerabilities.length > 0 && (
            <button
              className="scan-card__expand-button expand-button"
              onClick={handleExpandToggle}
              aria-expanded={isExpanded}
              aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
            >
              {isExpanded ? '▲ Less' : '▼ More'}
            </button>
          )}
          <button
            className="scan-card__view-button"
            onClick={(e) => {
              e.stopPropagation();
              if (onClick) onClick(scan_id, status);
            }}
            aria-label={`View full details for ${target_url}`}
            disabled={!isComplete}
          >
            {isComplete ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                View Details <ArrowRight size={18} strokeWidth={3} />
              </span>
            ) : 'Processing...'}
          </button>
        </div>
      </div>

      {/* Expandable Details */}
      {expandable && isExpanded && vulnerabilities && vulnerabilities.length > 0 && (
        <div className="scan-card__details" aria-live="polite">
          <h4 className="scan-card__details-title">
            Vulnerabilities ({vulnerabilities.length})
          </h4>
          <ul className="scan-card__vulnerability-list">
            {vulnerabilities.slice(0, 5).map((vuln, index) => (
              <li key={index} className="scan-card__vulnerability-item">
                <span className={`scan-card__vulnerability-severity scan-card__vulnerability-severity--${vuln.severity?.toLowerCase() || 'info'}`}>
                  {vuln.severity || 'Info'}
                </span>
                <span className="scan-card__vulnerability-title">
                  {vuln.title || vuln.name || 'Unnamed vulnerability'}
                </span>
              </li>
            ))}
            {vulnerabilities.length > 5 && (
              <li className="scan-card__vulnerability-item scan-card__vulnerability-item--more">
                +{vulnerabilities.length - 5} more vulnerabilities
              </li>
            )}
          </ul>
        </div>
      )}
    </article>
  );
};

export default ScanCard;

// Made with Bob
